/* ISQM access management (Partner-only) and the bundle verifier — moved out of pages/ISQM.jsx in split
   step 5b (28-Oct-2026), unchanged. */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import { useEffect, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import { fmtDateTimeFirm } from "@/lib/dt";
import api, { toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck, Users, UserPlus, X, AlertTriangle, Info,
  Download,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import EngagementPicker from "@/components/EngagementPicker";   // Step 11b · N1 (the label as Batch 15 · N5 F-119)
import { useAuth } from "@/contexts/AuthContext";


// ======================================================================
// Access management — Partner-only dialog to invite/revoke individuals
function AccessManageButton() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="isqm-manage-access-btn">
        <Users className="me-1 h-3.5 w-3.5" /> {t("isqmPage.manageAccess")}
      </Button>
      <AccessManageDialog open={open} onOpenChange={setOpen} />
    </>
  );
}


// ======================================================================
// Verify Bundle — upload an ISQM PDF and re-hash it against the record
// ======================================================================
function IsqmVerifyBundleButton() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error(t("isqmPage.toastVerifyPickPdf"));
      e.target.value = "";
      return;
    }
    setFile(f);
    setResult(null);
  };

  const verify = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/isqm/verify-bundle", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        _silent: true,
      });
      setResult(r.data);
      const v = r.data?.verdict;
      if (v === "PASS") toast.success(t("isqmPage.toastVerifyPass"));
      else if (v === "FAIL") toast.error(t("isqmPage.toastVerifyFail"));
      else if (v === "PARTIAL") toast.message(t("isqmPage.toastVerifyPartial"));   // Silent defaults · A-34
      else toast.message(t("isqmPage.toastVerifyUnknown"));
    } catch (e) {
      toast.error(toErrMsg(e, t("isqmPage.toastVerifyFailed")));
    } finally { setBusy(false); }
  };

  const reset = () => { setFile(null); setResult(null); };
  const v = result?.verdict;
  const cardCls =
    v === "PASS" ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800" :
    v === "FAIL" ? "border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800" :
    (v === "UNKNOWN" || v === "PARTIAL") ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800" : "";   // A-34: PARTIAL amber

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
              data-testid="isqm-verify-bundle-btn">
        <ShieldCheck className="me-1 h-3.5 w-3.5" /> {t("isqmPage.verifyBundleBtn")}
      </Button>
      <DialogContent className="max-w-2xl" data-testid="isqm-verify-bundle-dialog">
        <DialogHeader>
          <DialogTitle>{t("isqmPage.verifyTitle")}</DialogTitle>
          <DialogDescription>
            {t("isqmPage.verifyDesc")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 p-6 text-sm hover:border-emerald-500 hover:bg-emerald-50/40 dark:border-slate-700">
            <Download className="h-4 w-4 rotate-180" />
            <span>{file ? file.name : t("isqmPage.verifyChoose")}</span>
            <input type="file" accept=".pdf" className="hidden"
                   onChange={pick} disabled={busy}
                   data-testid="isqm-verify-file-input" />
          </label>
          <div className="flex gap-2">
            <Button onClick={verify} disabled={!file || busy}
                    data-testid="isqm-verify-run-btn">
              {busy ? t("isqmPage.verifyRunning") : t("isqmPage.verifyRun")}
            </Button>
            {(file || result) && (
              <Button variant="ghost" size="sm" onClick={reset}
                      data-testid="isqm-verify-reset-btn">{t("isqmPage.verifyReset")}</Button>
            )}
          </div>
          {result && (
            <div className={`rounded-md border p-4 text-sm ${cardCls}`}
                 data-testid="isqm-verify-result">
              <div className="flex items-center gap-2 text-base font-semibold">
                {v === "PASS" && <><ShieldCheck className="h-4 w-4" /> {t("isqmPage.verifyPass")}</>}
                {v === "FAIL" && <><AlertTriangle className="h-4 w-4" /> {t("isqmPage.verifyFail")}</>}
                {v === "UNKNOWN" && <><Info className="h-4 w-4" /> {t("isqmPage.verifyUnknown")}</>}
                {v === "PARTIAL" && <><AlertTriangle className="h-4 w-4" /> <span data-testid="isqm-verify-partial">{t("isqmPage.verifyPartial")}</span></>}
              </div>
              <div className="mt-2 text-xs opacity-90">{(lang === "ar" && result.message_ar) || result.message}</div>
              {result.bundle && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                  <div><span className="text-muted-foreground">{t("isqmPage.verifyBundleType")}</span><div className="font-mono">{result.bundle.bundle_type}</div></div>
                  <div><span className="text-muted-foreground">{t("isqmPage.verifyExported")}</span><div className="font-mono">{fmtDateTimeFirm(result.bundle.created_at)}</div></div>
                  <div><span className="text-muted-foreground">{t("isqmPage.verifyBy")}</span><div>{result.exporter?.name || "—"}{result.exporter?.role ? ` (${result.exporter.role})` : ""}</div></div>
                  {result.engagement ? (
                    <div><span className="text-muted-foreground">{t("isqmPage.verifyEngagement")}</span><div>{result.engagement.engagement_name || result.engagement.client_name} · {t("isqmPage.verifyEngagementFy")} {result.engagement.financial_year}</div></div>
                  ) : (
                    <div><span className="text-muted-foreground">{t("isqmPage.verifyScope")}</span><div>{t("isqmPage.verifyScopeFirmWide")}</div></div>
                  )}
                </div>
              )}
              <div className="mt-3 space-y-1 font-mono text-[10px] break-all">
                <div><span className="text-muted-foreground">{t("isqmPage.verifyRecomputed")}</span> {result.computed_file_sha256}</div>
                {result.bundle?.file_sha256 && <div><span className="text-muted-foreground">{t("isqmPage.verifyOnRecord")}</span> {result.bundle.file_sha256}</div>}
                {result.bundle?.signature_hash && <div><span className="text-muted-foreground">{t("isqmPage.verifySnapshotHash")}</span> {result.bundle.signature_hash}</div>}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t("isqmPage.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ======================================================================
// Access management — Partner-only dialog to invite/revoke individuals


function AccessManageDialog({ open, onOpenChange }) {
  const colW = useResizableColumns({ storageKey: "isqm-access-manage-dialog", defaults: [240, 110, 240, 160, 130], mins: [130, 72, 130, 100, 90] });   // Step 11d · add-on
  const { t, lang, dict } = useI18n();
  const { current } = useEngagement();
  const user = (useAuth() || {}).user;   // Step 11b · N1: whose recent engagements the picker shows first
  const [grants, setGrants] = useState([]);
  const [orphaned, setOrphaned] = useState([]);
  const [users, setUsers] = useState([]);
  const [, setLoading] = useState(false);
  const [scope, setScope] = useState("firm_isqm1");
  const [engId, setEngId] = useState(current?.id || "");
  const [userId, setUserId] = useState("");
  const [engagements, setEngagements] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [g, o, u, e] = await Promise.all([
        api.get("/isqm/access"),
        api.get("/isqm/access/orphaned"),
        api.get("/users"),
        api.get("/engagements"),
      ]);
      setGrants(g.data.grants || []);
      setOrphaned(o.data.orphaned || []);
      setUsers(u.data || []);
      setEngagements(asArray(e.data).filter((x) => x.requires_eqc));
    } catch (err) { toast.error(toErrMsg(err, t("isqmPage.toastLoadAccessFailed"))); }
    finally { setLoading(false); }
  };
  const loadOnOpen = useEffectEvent(load);
  useEffect(() => { if (open) loadOnOpen(); }, [open, loadOnOpen]);

  const grant = async () => {
    if (!userId) { toast.error(t("isqmPage.toastPickUser")); return; }
    if (scope === "engagement_isqm2" && !engId) { toast.error(t("isqmPage.toastPickEng")); return; }
    try {
      const body = { user_id: userId, scope };
      if (scope === "engagement_isqm2") body.engagement_id = engId;
      const r = await api.post("/isqm/access", body);
      if (r.data.already) toast.info(t("isqmPage.toastAccessAlready"));
      else toast.success(t("isqmPage.toastAccessGranted"));
      setUserId("");
      load();
    } catch (err) { toast.error(toErrMsg(err, t("isqmPage.toastGrantFailed"))); }
  };

  const revoke = async (gid) => {
    if (!await askConfirm(t("isqmPage.accessConfirmRevoke"))) return;
    try {
      await api.post(`/isqm/access/${gid}/revoke`);
      toast.success(t("isqmPage.toastRevoked"));
      load();
    } catch (err) { toast.error(toErrMsg(err, t("isqmPage.toastRevokeFailed"))); }
  };

  const active = grants.filter((g) => !g.revoked_at);
  const revoked = grants.filter((g) => g.revoked_at).slice(0, 30);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="isqm-access-dialog">
        <DialogHeader>
          <DialogTitle>{t("isqmPage.accessTitle")}</DialogTitle>
          <DialogDescription>
            {t("isqmPage.accessDescPre")} <b>{t("isqmPage.accessDescNotBold")}</b> {t("isqmPage.accessDescPost")}
          </DialogDescription>
        </DialogHeader>

        {/* Grant form */}
        <div className="grid grid-cols-1 gap-3 rounded-md border p-3 md:grid-cols-4">
          <div className="md:col-span-1">
            <Label>{t("isqmPage.accessLabelScope")}</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="isqm-grant-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="firm_isqm1">{t("isqmPage.accessScopeFirm")}</SelectItem>
                <SelectItem value="engagement_isqm2">{t("isqmPage.accessScopeEng")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {scope === "engagement_isqm2" && (
            <div className="md:col-span-1">
              <Label>{t("isqmPage.accessLabelEng")}</Label>
              {/* Step 11b · N1: the one searchable engagement picker */}
              <EngagementPicker engagements={engagements} value={engId} onChange={setEngId} userKey={user?.user_id || user?.id}
                                placeholder={t("isqmPage.accessPickPlaceholder")}
                                testId="isqm-grant-eng" optionTestId="isqm-grant-eng-option" />
              {engagements.length === 0 && (
                <p className="mt-1 text-[10px] text-amber-700">{t("isqmPage.accessNoEqc")}</p>
              )}
            </div>
          )}
          <div className={scope === "engagement_isqm2" ? "md:col-span-1" : "md:col-span-2"}>
            <Label>{t("isqmPage.accessLabelUser")}</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="isqm-grant-user"><SelectValue placeholder={t("isqmPage.accessPickPlaceholder")} /></SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} · {u.email} ({u.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end md:col-span-1">
            <Button onClick={grant} className="w-full bg-slate-900 hover:bg-slate-800"
                    data-testid="isqm-grant-submit">
              <UserPlus className="me-1 h-3.5 w-3.5" /> {t("isqmPage.accessGrant")}
            </Button>
          </div>
        </div>

        {/* Orphaned banner */}
        {orphaned.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
               data-testid="isqm-orphan-banner">
            <AlertTriangle className="me-1 inline h-3 w-3" />
            <b>{orphaned.length}</b> {fmtPlural(orphaned.length, lang, dict.plurals.isqmInvitations).replace(/^\d+\s*/, "")} {t("isqmPage.accessOrphanBody")}
          </div>
        )}

        {/* Active grants */}
        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{t("isqmPage.accessActiveGrants").replace("{count}", active.length)}</div>
          <div className="max-h-64 overflow-y-auto rounded-md border">
            <ResizableTable cols={colW} className="w-full text-xs">
              <thead className="border-b bg-slate-50 dark:bg-slate-900/60">
                <tr>
                  <ResizableTh index={0} className="px-3 py-2 text-start">{t("isqmPage.accessColUser")}</ResizableTh>
                  <ResizableTh index={1} className="px-3 py-2 text-start">{t("isqmPage.accessColRole")}</ResizableTh>
                  <ResizableTh index={2} className="px-3 py-2 text-start">{t("isqmPage.accessColScope")}</ResizableTh>
                  <ResizableTh index={3} className="px-3 py-2 text-start">{t("isqmPage.accessColGranted")}</ResizableTh>
                  <ResizableTh index={4} className="px-3 py-2 text-end">{t("isqmPage.accessColActions")}</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {active.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">{t("isqmPage.accessNoGrants")}</td></tr>
                )}
                {active.map((g) => {
                  const isOrphan = orphaned.some((o) => o.id === g.id);
                  return (
                    <tr key={g.id} className="border-b" data-testid={`isqm-grant-${g.id}`}>
                      <td className="px-3 py-2">{g.user_name} <span className="text-muted-foreground">· {g.user_email}</span></td>
                      <td className="px-3 py-2 text-muted-foreground">{g.user_underlying_role}</td>
                      <td className="px-3 py-2">
                        {g.scope === "firm_isqm1"
                          ? <Badge variant="outline">{t("isqmPage.accessScopeBadgeFirm")}</Badge>
                          : <Badge variant="outline">{t("isqmPage.accessScopeBadgeEngPrefix")} {g.engagement_id?.slice(0, 8)}</Badge>}
                      </td>
                      <td className="px-3 py-2">
                        <div>{g.granted_by_name}</div>
                        <div className="text-[10px] text-muted-foreground">{fmtDateTimeFirm(g.granted_at)}</div>
                        {isOrphan && <div className="text-[10px] font-medium text-amber-700">{t("isqmPage.accessOrphanNote")}</div>}
                      </td>
                      <td className="px-3 py-2 text-end">
                        <Button size="sm" variant="ghost" onClick={() => revoke(g.id)}
                                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                data-testid={`isqm-revoke-${g.id}`}>
                          <X className="me-1 h-3 w-3" /> {t("isqmPage.accessRevoke")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </ResizableTable>
          </div>
        </div>

        {/* Revoked history */}
        {revoked.length > 0 && (
          <details>
            <summary className="cursor-pointer text-[11px] text-muted-foreground">
              {t("isqmPage.accessRevokedHistory").replace("{count}", revoked.length)}
            </summary>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-md border">
              <table className="w-full text-[11px]">
                <tbody>
                  {revoked.map((g) => (
                    <tr key={g.id} className="border-b text-muted-foreground">
                      <td className="px-3 py-1.5">{g.user_name} · {g.scope === "firm_isqm1" ? t("isqmPage.accessRevokedFirm") : `${t("isqmPage.accessRevokedEngPrefix")} ${g.engagement_id?.slice(0,8)}`}</td>
                      <td className="px-3 py-1.5">{t("isqmPage.accessRevokedBy").replace("{name}", g.revoked_by_name).replace("{date}", fmtDateTimeFirm(g.revoked_at))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("isqmPage.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { AccessManageButton, IsqmVerifyBundleButton };
